import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { CheckCircle, CreditCard, Person, Sports, School } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useOnboarding } from '../hooks/useOnboarding'
import { useSubscription } from '../hooks/useSubscription'
import { useRecruiterProfile } from '../hooks/useRecruiterProfile'
import { ProfileWizardPage } from './ProfileWizardPage'
import { AppPageHeader } from '../components/AppPageHeader'
import { LoadingStatePanel } from '../components/StatePanel'
import {
  LIMITED_TIME_PRICING_LABEL,
  RENEWAL_DISCLOSURE,
  formatDiscountedRecruiterPrice,
  formatRecruiterPrice,
} from '../lib/pricing'
import { LaunchOfferChip } from '../components/LaunchOfferChip'
import { OfferPrice } from '../components/OfferPrice'
import { useMyLaunchOffer } from '../hooks/useLaunchOffer'

export const OnboardingPage = () => {
  const navigate = useNavigate()
  const {
    isStudent,
    isRecruiter,
    hasPaid,
    profileComplete,
    onboardingComplete,
    studentCount,
    studentCountLoading,
    profileLoading,
    createStudentCheckout,
  } = useOnboarding()
  const { createCheckout } = useSubscription()
  const { profile: recruiterProfile } = useRecruiterProfile({ enabled: isRecruiter })
  const recruiterDivision = recruiterProfile.data?.school?.division ?? null

  const studentNeedsPayment = isStudent && studentCount >= 1000 && !hasPaid
  const recruiterNeedsPayment = isRecruiter && !hasPaid
  const launchOffer = useMyLaunchOffer({ enabled: recruiterNeedsPayment })
  const launchDiscount =
    launchOffer?.eligible && launchOffer.percentOff ? launchOffer.percentOff : null

  const showPaymentStep = studentNeedsPayment || recruiterNeedsPayment
  const steps = showPaymentStep ? ['Payment', 'Complete Your Profile'] : ['Complete Your Profile']

  const handleRecruiterCheckout = async () => {
    try {
      const result = await createCheckout.mutateAsync({
        successUrl: `${window.location.origin}/onboarding?success=true`,
        cancelUrl: `${window.location.origin}/onboarding?canceled=true`,
      })
      if (result.url) window.location.href = result.url
    } catch {
      // Error handled via mutation state
    }
  }

  const handleStudentCheckout = async () => {
    try {
      const result = await createStudentCheckout.mutateAsync({
        successUrl: `${window.location.origin}/onboarding?success=true`,
        cancelUrl: `${window.location.origin}/onboarding?canceled=true`,
      })
      if (result.url) window.location.href = result.url
    } catch {
      // Error handled via mutation state
    }
  }

  const handleFinish = () => {
    navigate('/dashboard')
  }

  if (onboardingComplete) {
    navigate('/dashboard', { replace: true })
    return null
  }

  if (studentCountLoading && isStudent) {
    return (
      <LoadingStatePanel
        title="Loading onboarding"
        description="Checking your account setup and eligibility."
      />
    )
  }

  return (
    <Box sx={{ pb: 4, maxWidth: 800, mx: 'auto', px: 2 }}>
      {isStudent && studentCount < 1000 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          You're one of our first 1,000 athletes — free access! No payment required.
        </Alert>
      )}

      <AppPageHeader
        title="Welcome! Let's get you set up."
        description={
          isStudent
            ? 'Complete these steps to create your athlete profile.'
            : 'Complete these steps to access our athlete database.'
        }
      />

      {showPaymentStep && (
        <>
          <Stepper activeStep={0} sx={{ mb: 4 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Card>
            <CardContent>
              {isStudent ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    One-Time Access Fee
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Pay once to create and publish your athlete profile for recruiters.
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Sports color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Unlimited profile updates" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Visible to college recruiters" />
                    </ListItem>
                  </List>
                  {createStudentCheckout.isError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      Failed to create checkout. Please try again.
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<CreditCard />}
                    onClick={handleStudentCheckout}
                    disabled={createStudentCheckout.isPending}
                    sx={{ mt: 2 }}
                  >
                    {createStudentCheckout.isPending ? 'Redirecting...' : 'Pay Now'}
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    Recruiter Subscription
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    Subscribe to access our database of qualified student-athletes.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      <OfferPrice
                        original={formatRecruiterPrice(recruiterDivision)}
                        discounted={
                          launchDiscount
                            ? formatDiscountedRecruiterPrice(recruiterDivision, launchDiscount)
                            : undefined
                        }
                      />
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}
                        / {launchDiscount ? 'first year' : 'year'}
                      </Typography>
                    </Typography>
                    <Chip size="small" color="warning" label={LIMITED_TIME_PRICING_LABEL} />
                    {launchDiscount && <LaunchOfferChip offer={launchOffer} size="small" />}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 2 }}
                  >
                    {RENEWAL_DISCLOSURE}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <School color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Unlimited athlete profiles" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Advanced search & filters" />
                    </ListItem>
                  </List>
                  {createCheckout.isError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      Failed to create checkout. Please try again.
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<CreditCard />}
                    onClick={handleRecruiterCheckout}
                    disabled={createCheckout.isPending}
                    sx={{ mt: 2 }}
                  >
                    {createCheckout.isPending ? 'Redirecting...' : 'Subscribe Now'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {!showPaymentStep && (
        <Box>
          {profileLoading ? (
            <LoadingStatePanel
              title="Loading profile section"
              description="Preparing your onboarding profile requirements."
            />
          ) : (
            <>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Person color="primary" />
                Complete Your Profile
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isStudent
                  ? 'Please complete the following so recruiters can find you.'
                  : 'Add your school, position, and recruiting focus.'}
              </Typography>

              <Box sx={{ px: 2 }}>
                <ProfileWizardPage />
              </Box>

              {profileComplete && (
                <Box sx={{ mt: 3 }}>
                  <Alert severity="success" icon={<CheckCircle />}>
                    <Typography variant="body2" component="div">
                      <strong>Welcome — you&apos;re ready.</strong> Your profile looks complete. Go
                      to your dashboard to see recruiting progress, update PRs, and keep your info
                      current.
                    </Typography>
                  </Alert>
                  <Button variant="contained" onClick={handleFinish} sx={{ mt: 2 }}>
                    Go to Dashboard
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  )
}
